import React from 'react';
import { v4 as uuid } from 'uuid';
import 'twin.macro';
import { EleveGroup } from 'types/eleveGroups';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import {
  PencilIcon,
  PlusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Eleve } from 'types/eleves';
import Popover from 'components/Popover';
import { GroupEditorPanel } from './GroupEditorPanel';
import { randomMediumColor } from 'components/Form/ColorSelector';
import { useTranslation } from 'react-i18next';
import { GroupTag } from 'containers/CahierJournal/components/GroupTag';
import { IndividualElevesPanel } from './IndividualElevesPanel';
import usePopoverContext from 'components/Popover/usePopoverContext';
import {
  handleEphemeralGroupSubmit,
  updateEphemeralGroup,
} from './groupHandlers';

// Wrapper component to access Popover context
function EphemeralGroupEditor({
  group,
  eleves,
  groups,
  pendingEphemeralGroups,
  onUpdateGroup,
  onDeleteGroup,
  onPendingEphemeralGroupsChange,
}: {
  group: EleveGroup;
  eleves: Eleve[];
  groups: EleveGroup[];
  pendingEphemeralGroups: Array<{ id: string; elevesIds: string[] }>;
  onUpdateGroup: (g: EleveGroup) => void;
  onDeleteGroup: (id: string) => void;
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void;
}) {
  const { close } = usePopoverContext();

  return (
    <IndividualElevesPanel
      eleves={eleves}
      selectedEleveIds={group.eleves.map(e => e.id)}
      onSubmit={selectedIds => {
        updateEphemeralGroup(
          group.id,
          selectedIds,
          groups,
          pendingEphemeralGroups,
          eleves,
          onUpdateGroup,
          onPendingEphemeralGroupsChange,
        );
      }}
      onClose={() => {
        close?.();
      }}
      onDelete={() => onDeleteGroup(group.id)}
      isEditing={true}
    />
  );
}

// Wrapper component for creating ephemeral groups
function EphemeralGroupCreator({
  eleves,
  groups,
  onPendingEphemeralGroupsChange,
  pendingEphemeralGroups,
  onUpdateGroup,
  selectedGroupIds,
  onChangeSelected,
}: {
  eleves: Eleve[];
  groups: EleveGroup[];
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void;
  pendingEphemeralGroups: Array<{ id: string; elevesIds: string[] }>;
  onUpdateGroup: (g: EleveGroup) => void;
  selectedGroupIds: string[];
  onChangeSelected: (newSelected: string[]) => void;
}) {
  const { close } = usePopoverContext();

  return (
    <IndividualElevesPanel
      eleves={eleves}
      selectedEleveIds={[]}
      isEditing={false}
      onSubmit={selectedIds => {
        // Create the ephemeral group and get its ID
        const ephemeralGroupId = handleEphemeralGroupSubmit(
          selectedIds,
          undefined, // No editingGroupId for creation
          groups,
          pendingEphemeralGroups,
          eleves,
          onUpdateGroup,
          onPendingEphemeralGroupsChange,
        );

        // Auto-select the newly created ephemeral group
        if (ephemeralGroupId) {
          onChangeSelected([...selectedGroupIds, ephemeralGroupId]);
        }
      }}
      onClose={() => {
        close?.();
      }}
    />
  );
}

interface GroupSelectorProps {
  allGroups: EleveGroup[];
  selectedGroupIds: string[];
  onChangeSelected: (newSelected: string[]) => void;
  eleves: Eleve[];
  groups: EleveGroup[];
  pendingEphemeralGroups: Array<{ id: string; elevesIds: string[] }>;
  onCreateGroup: (g: EleveGroup) => void;
  onUpdateGroup: (g: EleveGroup) => void;
  onDeleteGroup: (id: string) => void;
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void;
}

export function GroupSelectorInline({
  allGroups,
  selectedGroupIds,
  onChangeSelected,
  onCreateGroup,
  eleves,
  groups,
  pendingEphemeralGroups,
  onUpdateGroup,
  onDeleteGroup,
  onPendingEphemeralGroupsChange,
}: GroupSelectorProps) {
  const { t } = useTranslation(['cahierJournal']);
  const handleToggleGroup = (groupId: string) => {
    const alreadySelected = selectedGroupIds.includes(groupId);
    if (alreadySelected) {
      onChangeSelected(selectedGroupIds.filter(id => id !== groupId));
    } else {
      onChangeSelected([...selectedGroupIds, groupId]);
    }
  };
  return (
    <div tw="space-y-1">
      <div tw="flex flex-wrap items-center gap-x-4 gap-y-2">
        {allGroups.map(group => {
          const isChecked = selectedGroupIds.includes(group.id);
          return (
            <div key={group.id} tw="flex items-center text-sm gap-x-1">
              <Checkbox
                id={group.id}
                checked={isChecked}
                onChange={() => handleToggleGroup(group.id)}
                label={
                  <GroupTag
                    key={group.id}
                    color={group.color}
                    groupName={group.name}
                    isEphemeral={group.isEphemeral}
                    groupEleves={group.eleves}
                  />
                }
              />
              <Popover portal>
                <Popover.Button minimal small tw="px-1" icon={PencilIcon} />
                <Popover.Panel size="lg">
                  <div tw="px-4 max-h-[20rem] overflow-y-auto">
                    {group.isEphemeral ? (
                      <EphemeralGroupEditor
                        group={group}
                        eleves={eleves}
                        groups={groups}
                        pendingEphemeralGroups={pendingEphemeralGroups}
                        onUpdateGroup={onUpdateGroup}
                        onDeleteGroup={onDeleteGroup}
                        onPendingEphemeralGroupsChange={
                          onPendingEphemeralGroupsChange
                        }
                      />
                    ) : (
                      <GroupEditorPanel
                        onDeleteGroup={onDeleteGroup}
                        eleves={eleves}
                        group={{
                          id: group.id,
                          name: group.name,
                          color: group.color,
                          isEphemeral: group.isEphemeral,
                          eleves: group.eleves,
                        }}
                        onSubmit={g => {
                          onUpdateGroup({
                            ...g,
                            id: g.id!,
                          });
                        }}
                      />
                    )}
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          );
        })}
        {/* Add new group button */}
        <Popover portal>
          <Popover.Button
            small
            tw="justify-self-end"
            iconSize="sm"
            icon={PlusIcon}
          >
            {t('ateliers.createGroup')}
          </Popover.Button>
          <Popover.Panel size="lg">
            <div tw="max-h-[20rem] overflow-y-auto">
              <GroupEditorPanel
                group={{
                  name: '',
                  color: randomMediumColor(),
                  eleves: [],
                }}
                eleves={eleves}
                onDeleteGroup={() => {}}
                onSubmit={g => {
                  // Because EleveGroup requires an id, generate one:
                  const newGroup: EleveGroup = {
                    ...g,
                    id: uuid(),
                  };
                  onCreateGroup(newGroup);
                }}
              />
            </div>
          </Popover.Panel>
        </Popover>

        {/* Add individual students button */}
        <Popover portal>
          <Popover.Button
            tw="justify-self-end"
            small
            iconSize="sm"
            icon={UserPlusIcon}
          >
            {t('ateliers.addIndividualStudents')}
          </Popover.Button>
          <Popover.Panel size="lg">
            <div tw="max-h-[20rem] overflow-y-auto px-4">
              <EphemeralGroupCreator
                eleves={eleves}
                groups={groups}
                pendingEphemeralGroups={pendingEphemeralGroups}
                onPendingEphemeralGroupsChange={onPendingEphemeralGroupsChange}
                onUpdateGroup={onUpdateGroup}
                selectedGroupIds={selectedGroupIds}
                onChangeSelected={onChangeSelected}
              />
            </div>
          </Popover.Panel>
        </Popover>
      </div>
    </div>
  );
}
